import React, { useState, Children } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Lock, Mail, User as UserIcon, Eye, EyeOff, AlertCircle, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { createCheckoutSession, redirectToCheckout } from '../../../utils/stripe';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutLineItemInput } from '../../../utils/stripe';
// Build a Stripe-compatible line item reference from a cart item.
// Priority: 1) lookupKey on the item, 2) stripePriceId on the item,
//           3) shopData lookup (supplements), 4) null (not buyable yet).
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) return {
    lookupKey: item.lookupKey
  };
  if (item.stripePriceId) return {
    priceId: item.stripePriceId
  };
  const supplement = shopData.find(p => p.id === item.productId);
  if (supplement?.stripePriceId) return {
    priceId: supplement.stripePriceId
  };
  return null;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}
function mapAuthError(err: any): string {
  const code = err?.code ?? '';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Sign in instead.';
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') return 'Invalid email or password.';
  if (code === 'auth/weak-password') return 'Password must be at least 6 characters.';
  if (code === 'auth/invalid-email') return 'Please enter a valid email.';
  return err?.message ?? 'Something went wrong. Please try again.';
}
type Mode = 'signin' | 'signup';
export function Checkout() {
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signIn,
    signUp
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [mode, setMode] = useState<Mode>('signup');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Empty cart guard
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen bg-bodybg" data-id="element-1517">
        <section className="bg-bodybg pt-40 pb-20 px-6 border-b border-darkgray" data-id="element-1518">
          <div className="max-w-2xl mx-auto text-center" data-id="element-1519">
            <ShoppingBag className="w-12 h-12 text-tealslate mx-auto mb-6" data-id="element-1520" />
            <h1 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1521">
              Your cart is empty.
            </h1>
            <p className="text-[#D8D8D0] mb-10 leading-relaxed" data-id="element-1522">
              Add a book or supplement to begin checkout.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1523">
              <Link to="/books" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2" data-id="element-1524">
                Browse Books <ArrowRight className="w-4 h-4" data-id="element-1525" />
              </Link>
              <Link to="/shop" className="px-8 py-4 border border-tealslate text-tealslate hover:bg-tealslate hover:text-white text-sm font-bold uppercase tracking-wider transition-colors inline-flex items-center justify-center gap-2" data-id="element-1526">
                Shop Supplements <ArrowRight className="w-4 h-4" data-id="element-1527" />
              </Link>
            </div>
          </div>
        </section>
      </div>;
  }
  // Auth gate passed: show the payment step
  if (user && !authLoading) {
    return <PaymentStep data-id="element-1528" />;
  }
  // While auth is resolving, show a quiet loading state
  if (authLoading) {
    return <div className="w-full min-h-screen bg-bodybg flex items-center justify-center" data-id="element-1529">
        <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin" data-id="element-1530" />
      </div>;
  }
  // Auth gate form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name.');
          setSubmitting(false);
          return;
        }
        await signUp(email, password, name.trim(), marketingOptIn);
      } else {
        await signIn(email, password);
      }
      // Stay on /checkout — user is now signed in, the if(user) branch above renders
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1531">
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-1532">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1533">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-1534">
            <motion.p variants={fadeUp} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1535">
              Checkout
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1536">
              {mode === 'signup' ? 'Create an account to continue.' : 'Sign in to continue.'}
            </motion.h1>
            <motion.div variants={fadeUp} className="w-16 h-0.5 bg-tealslate mx-auto mb-6" data-id="element-1537" />
            <motion.p variants={fadeUp} className="text-base text-[#D8D8D0] max-w-xl mx-auto leading-relaxed" data-id="element-1538">
              An account lets you track orders, access digital downloads, and
              save info for next time.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="bg-[#F5F5F0] py-16 md:py-24 px-6" data-id="element-1539">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12" data-id="element-1540">
          {/* Form */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-3" data-id="element-1541">
            {/* Mode toggle */}
            <div className="flex bg-white border border-[#D8D8D0] mb-8" data-id="element-1542">
              <button type="button" onClick={() => {
              setMode('signup');
              setError(null);
            }} className={`flex-1 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${mode === 'signup' ? 'bg-tealslate text-white' : 'text-[#3B3B3B] hover:bg-[#F5F5F0]'}`} data-id="element-1543">
                New Customer
              </button>
              <button type="button" onClick={() => {
              setMode('signin');
              setError(null);
            }} className={`flex-1 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors ${mode === 'signin' ? 'bg-tealslate text-white' : 'text-[#3B3B3B] hover:bg-[#F5F5F0]'}`} data-id="element-1544">
                Returning Customer
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1545">
              {mode === 'signup' && <div data-id="element-1546">
                  <label htmlFor="name" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1547">
                    Full Name
                  </label>
                  <div className="relative" data-id="element-1548">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1549" />
                    <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="Jane Smith" data-id="element-1550" />
                  </div>
                </div>}

              <div data-id="element-1551">
                <label htmlFor="email" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1552">
                  Email
                </label>
                <div className="relative" data-id="element-1553">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1554" />
                  <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder="you@example.com" data-id="element-1555" />
                </div>
              </div>

              <div data-id="element-1556">
                <label htmlFor="password" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-1557">
                  Password
                </label>
                <div className="relative" data-id="element-1558">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A90]" data-id="element-1559" />
                  <input id="password" type={showPassword ? 'text' : 'password'} required minLength={mode === 'signup' ? 6 : undefined} value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-white border border-[#D8D8D0] pl-11 pr-12 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'} data-id="element-1560" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9A9A90] hover:text-tealslate transition-colors" tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-1561">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-1562" /> : <Eye className="w-4 h-4" data-id="element-1563" />}
                  </button>
                </div>
                {mode === 'signin' && <div className="mt-3 text-right" data-id="element-1564">
                    <Link to="/forgot-password" className="text-xs font-bold text-tealslate uppercase tracking-[0.2em] hover:text-navyslate transition-colors" data-id="element-1565">
                      Forgot password?
                    </Link>
                  </div>}
              </div>

              {mode === 'signup' && <label className="flex items-start gap-3 cursor-pointer" data-id="element-1566">
                  <input type="checkbox" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 w-4 h-4 accent-tealslate cursor-pointer" data-id="element-1567" />
                  <span className="text-sm text-[#3B3B3B] leading-relaxed" data-id="element-1568">
                    Send me updates on new books, supplements, and articles.
                  </span>
                </label>}

              {error && <div className="bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1569">
                  <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1570" />
                  <p className="text-sm text-burgundy" data-id="element-1571">{error}</p>
                </div>}

              <button type="submit" disabled={submitting} className="w-full px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3" data-id="element-1572">
                {submitting ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1573" />
                    {mode === 'signup' ? 'Creating account…' : 'Signing in…'}
                  </> : <>
                    {mode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
                    <ArrowRight className="w-4 h-4" data-id="element-1574" />
                  </>}
              </button>
            </form>
          </motion.div>

          {/* Order Summary */}
          <motion.aside initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="lg:col-span-2" data-id="element-1575">
            <CartSummary data-id="element-1576" />
          </motion.aside>
        </div>
      </section>
    </div>;
}
function PaymentStep() {
  const {
    user
  } = useAuth();
  const {
    items,
    subtotal
  } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unbuyable = items.filter(i => !getCheckoutRefFor(i));
  const handleCheckout = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const lineItems = items.map(i => {
        const ref = getCheckoutRefFor(i);
        if (!ref) return null;
        return {
          ...ref,
          quantity: i.quantity
        };
      }).filter((x): x is NonNullable<typeof x> => x !== null);
      if (lineItems.length === 0) {
        setError('No items in your cart are available for purchase yet.');
        setSubmitting(false);
        return;
      }
      const origin = window.location.origin;
      const {
        url
      } = await createCheckoutSession({
        lineItems,
        successUrl: origin + '/account?order=success',
        cancelUrl: origin + '/checkout'
      });
      redirectToCheckout(url);
    } catch (err: any) {
      setError(err?.message ?? 'Something went wrong starting checkout.');
      setSubmitting(false);
    }
  };
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1577">
      <section className="bg-bodybg pt-40 pb-12 px-6 border-b border-darkgray" data-id="element-1578">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1579">
          <p className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1580">
            Checkout
          </p>
          <h1 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-1581">
            Review your order.
          </h1>
          <div className="w-16 h-0.5 bg-tealslate mx-auto mb-6" data-id="element-1582" />
          <p className="text-base text-[#D8D8D0] max-w-xl mx-auto leading-relaxed" data-id="element-1583">
            Signed in as <span className="text-tealslate" data-id="element-1584">{user?.email}</span>.
            Click below to pay securely with Stripe.
          </p>
        </div>
      </section>

      <section className="bg-[#F5F5F0] py-16 md:py-24 px-6" data-id="element-1585">
        <div className="max-w-3xl mx-auto" data-id="element-1586">
          <CartSummary data-id="element-1587" />

          {unbuyable.length > 0 && <div className="mt-6 bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1588">
              <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1589" />
              <div className="text-sm text-burgundy" data-id="element-1590">
                <p className="font-bold mb-1" data-id="element-1591">
                  Some items aren't available for purchase yet:
                </p>
                <ul className="list-disc ml-5" data-id="element-1592">
                  {unbuyable.map(i => <li key={i.productId} data-id="element-1593">{i.name}</li>)}
                </ul>
              </div>
            </div>}

          {error && <div className="mt-6 bg-burgundy/10 border-l-4 border-burgundy p-4 flex gap-3 items-start" data-id="element-1594">
              <AlertCircle className="w-5 h-5 text-burgundy shrink-0 mt-0.5" data-id="element-1595" />
              <p className="text-sm text-burgundy" data-id="element-1596">{error}</p>
            </div>}

          <button type="button" onClick={handleCheckout} disabled={submitting || items.length === 0} className="mt-8 w-full px-8 py-5 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3" data-id="element-1597">
            {submitting ? <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1598" />
                Redirecting to Stripe…
              </> : <>
                <Lock className="w-4 h-4" data-id="element-1599" />
                Pay {formatPrice(subtotal)} with Stripe
              </>}
          </button>

          <p className="text-xs text-[#7A7A70] italic text-center mt-4" data-id="element-1600">
            Secure payment processed by Stripe. You'll return here when done.
          </p>
        </div>
      </section>
    </div>;
}
function CartSummary() {
  const {
    items,
    subtotal
  } = useCart();
  return <div className="bg-charcoal text-white p-8 border-l-4 border-tealslate" data-id="element-1601">
      <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-5" data-id="element-1602">
        Order Summary
      </p>
      <ul className="space-y-4 mb-6 pb-6 border-b border-white/10" data-id="element-1603">
        {items.map(item => <li key={item.productId} className="flex justify-between items-start gap-4 text-sm" data-id="element-1604">
            <div data-id="element-1605">
              <p className="font-serif text-base text-white" data-id="element-1606">{item.name}</p>
              <p className="text-xs text-[#9CA3AF] mt-1" data-id="element-1607">
                Qty {item.quantity} ·{' '}
                {item.type === 'book' ? 'Digital' : 'Physical'}
              </p>
            </div>
            <p className="text-white font-bold whitespace-nowrap" data-id="element-1608">
              {formatPrice(item.unitPrice * item.quantity)}
            </p>
          </li>)}
      </ul>
      <div className="flex items-center justify-between mb-2" data-id="element-1609">
        <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.25em]" data-id="element-1610">
          Subtotal
        </span>
        <span className="text-2xl font-serif text-white" data-id="element-1611">
          {formatPrice(subtotal)}
        </span>
      </div>
      <p className="text-xs text-[#9CA3AF] italic" data-id="element-1612">
        Shipping and tax calculated at payment.
      </p>
    </div>;
}